/**
 * Timed building upgrades: the `upgrade` command pays and sets
 * `Building.upgrading` (commands.ts `handleUpgrade`); this system advances it
 * by `dt` and applies the new level at the target's `upgradeTime`
 * (`completeUpgrade`). Until then the building keeps its old level and stats
 * (a barracks' units stay locked), and several buildings upgrade at once.
 * Progress pauses while the building is under attack (damaged within
 * `UNDER_ATTACK_TICKS`): the upgrade isn't lost but can't finish mid-siege.
 * No worker is needed. An upgrade ends with its building: destroyed, it is
 * lost (a razer is rewarded for what was paid, combat.ts `raze`), and
 * buildings never change hands, so nothing is inherited.
 * It runs right after construction and before the economy, so a finished
 * farm level gathers at its new rate the same tick.
 */
import { bonusCity, buildingMaxHp } from '../bonuses';
import { invalidateDerived } from '../derivedRegistry';
import { toMilli } from '../fixed';
import { growBuilding } from '../footprint';
import { buildingLevel, levelDef } from '../levels';
import { UNDER_ATTACK_TICKS } from '../placement';
import { deposit, gatherDepositCity } from '../stock';
import { territoryOwners } from '../territory';
import type { Building, EntityId, PlayerId, SimContext, SimState, SystemFn } from '../types';

/**
 * Raises `b` to its `upgrading` level: hp rises by the gain in max hp, and so
 * does the hp of the city it boosts (barracks); a territory building records
 * the tick for its new ring (territory.ts); a growing building takes the
 * extra tiles its new level allows. Clears `upgrading`.
 */
function completeUpgrade(state: SimState, ctx: SimContext, b: Building): void {
  const level = b.upgrading?.level;
  delete b.upgrading;
  const def = ctx.rules.buildings[b.type];
  if (level === undefined || !def) return;
  const city = bonusCity(state, ctx.rules, b);
  const before = buildingMaxHp(state, ctx.rules, b);
  const cityBefore = city ? buildingMaxHp(state, ctx.rules, city) : 0;
  b.level = level;
  b.hp += buildingMaxHp(state, ctx.rules, b) - before;
  if (city) city.hp += buildingMaxHp(state, ctx.rules, city) - cityBefore;
  // Grown territory rings are claimed in upgrade order (territory.ts).
  if (def.territoryRadius !== undefined) b.levelTicks = [...(b.levelTicks ?? []), state.tick];
  // A farm or forestry spreads over nearby matching land (footprint.ts).
  if (def.growth) growBuilding(state, ctx, b);
  invalidateDerived(state);
}

/**
 * Cancels `playerId`'s upgrade in progress at `buildingId` (the
 * `cancelUpgrade` command): `paid` is refunded in full, deposited like
 * gathered resources (see `gatherDepositCity`; lost with no city), and the
 * level stays. False when there's no such upgrade of theirs.
 */
function cancelUpgrade(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  buildingId: EntityId
): boolean {
  const b = state.buildings[buildingId];
  if (!b || b.owner !== playerId || !b.upgrading) return false;
  const { paid } = b.upgrading;
  delete b.upgrading;
  const city = gatherDepositCity(
    state,
    ctx.rules,
    playerId,
    b.pos,
    territoryOwners(state, ctx.rules)
  );
  if (city === undefined) return true;
  for (const res of Object.keys(paid).sort()) deposit(state, city, res, paid[res]);
  return true;
}

const upgrades: SystemFn = (state, ctx, _inputs, dt) => {
  const step = toMilli(dt);
  for (const b of Object.values(state.buildings)) {
    const up = b.upgrading;
    if (!up) continue;
    const def = ctx.rules.buildings[b.type];
    const next = def && levelDef(def, up.level);
    // A snapshot's stale upgrade (level already reached, gone from the rules, or on a site) is dropped.
    if (!next || up.level !== buildingLevel(b) + 1 || b.buildProgress < 1) {
      delete b.upgrading;
      continue;
    }
    if (b.lastDamagedTick !== undefined && state.tick - b.lastDamagedTick < UNDER_ATTACK_TICKS) {
      continue;
    }
    const time = toMilli(next.upgradeTime);
    up.progress = Math.min(time, up.progress + step);
    if (up.progress >= time) completeUpgrade(state, ctx, b);
  }
};

export { cancelUpgrade, completeUpgrade, upgrades };
